import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DeckCheck,
  DeckColumn,
  DeckColumnHeader,
  DeckOption,
  DecksColumnsContainer,
  SectionTitle,
  TwoUp,
} from "./GameSettingsModal.styles";
import { useNewGame } from "../../hooks/Game/useNewGame";
import { PATHS } from "../../routes";
import { SettingsData } from "../../types/GameState";
import { useListDecks } from "../../hooks/CRUD/deck/useListDecks";
import { useListCards } from "../../hooks/CRUD/card/useListCards";
import { CARD_TYPE } from "../../types/Card";
import { useUpdateGameSettings } from "../../hooks/Game/useUpdateGameSettings";
import PlayerIconSelector from "../PlayerIconSelector/PlayerIconSelector";
import ModalCard from "../ui/ModalCard";
import Icon from "../ui/Icon";
import Tag from "../ui/Tag";

type GameSettingsModalProps = {
  visible?: boolean;
  onClose?: () => void;
  code?: string;
  settings?: SettingsData;
  isAdmin?: boolean;
};

const GameSettingsModal: React.FC<GameSettingsModalProps> = ({
  visible = false,
  onClose,
  code,
  settings,
  isAdmin,
}) => {
  const [name, setName] = useState("");
  const [maxPlayers, setMaxPlayers] = useState(10);
  const [voteTime, setVoteTime] = useState(0);
  const [answerTime, setAnswerTime] = useState(0);
  const [password, setPassword] = useState("");
  const [avatar, setAvatar] = useState({ color: "", animal: "" });
  const [selectedAnswerDecks, setSelectedAnswerDecks] = useState<{
    [deckId: string]: boolean;
  }>({});
  const [selectedQuestionDecks, setSelectedQuestionDecks] = useState<{
    [deckId: string]: boolean;
  }>({});
  const [answerDeckMap, setAnswerDeckMap] = useState<{
    [deckId: string]: number;
  }>({});
  const [questionDeckMap, setQuestionDeckMap] = useState<{
    [deckId: string]: number;
  }>({});

  const isEditing = !!code;

  const { decks } = useListDecks(isEditing);
  const { cards: allQuestionCards } = useListCards(
    CARD_TYPE.QUESTION,
    isEditing,
  );
  const { cards: allAnswerCards } = useListCards(CARD_TYPE.ANSWER, isEditing);

  const { loading: newLoading, error: newError, newGame } = useNewGame();
  const {
    loading: updateLoading,
    error: updateError,
    updateGameSettings,
  } = useUpdateGameSettings();

  const loading = newLoading || updateLoading;
  const error = newError || updateError;

  const totalQuestionCardsCount = useMemo(
    () =>
      allQuestionCards?.filter(({ id: cardId }) =>
        decks?.find(
          ({ id: deckId, cards }) =>
            selectedQuestionDecks[deckId] && cards.includes(cardId),
        ),
      ).length || 0,
    [allQuestionCards, decks, selectedQuestionDecks],
  );

  const totalAnswerCardsCount = useMemo(
    () =>
      allAnswerCards?.filter(({ id: cardId }) =>
        decks?.find(
          ({ id: deckId, cards }) =>
            selectedAnswerDecks[deckId] && cards.includes(cardId),
        ),
      ).length || 0,
    [allAnswerCards, decks, selectedAnswerDecks],
  );

  const valid = useMemo(
    () =>
      name?.length > 3 &&
      (isEditing ||
        (totalQuestionCardsCount >= 10 && totalAnswerCardsCount >= 50)) &&
      maxPlayers >= 1 &&
      maxPlayers <= 15 &&
      (!password || password.length > 3),
    [
      name?.length,
      isEditing,
      totalQuestionCardsCount,
      totalAnswerCardsCount,
      maxPlayers,
      password,
    ],
  );

  useEffect(() => {
    const answers: any =
      allAnswerCards?.reduce(
        (mem, card) => ({
          ...mem,
          [card.id]: true,
        }),
        {},
      ) || {};
    const questions: any =
      allQuestionCards?.reduce(
        (mem, card) => ({
          ...mem,
          [card.id]: true,
        }),
        {},
      ) || {};

    setAnswerDeckMap(
      (decks || []).reduce(
        (mem, { id, cards }) => ({
          ...mem,
          [id]: cards.filter((card) => answers[card]).length,
        }),
        {},
      ),
    );
    setQuestionDeckMap(
      (decks || []).reduce(
        (mem, { id, cards }) => ({
          ...mem,
          [id]: cards.filter((card) => questions[card]).length,
        }),
        {},
      ),
    );
  }, [allAnswerCards, allQuestionCards, decks]);

  const resetDecks = useCallback(() => {
    setSelectedAnswerDecks(
      (decks || []).reduce(
        (mem, { id }) => ({
          ...mem,
          [id]: true,
        }),
        {},
      ),
    );
    setSelectedQuestionDecks(
      (decks || []).reduce(
        (mem, { id }) => ({
          ...mem,
          [id]: true,
        }),
        {},
      ),
    );
  }, [decks]);

  const reset = useCallback(() => {
    setName(localStorage.getItem("name") || "");
    if (settings) {
      setMaxPlayers(settings.maxPlayers);
      setVoteTime(settings.voteTime);
      setAnswerTime(settings.answerTime);
      setPassword(settings.password);
    } else {
      setMaxPlayers(10);
      setVoteTime(0);
      setAnswerTime(0);
      setPassword("");
      resetDecks();
    }
  }, [resetDecks, settings]);

  useEffect(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    if (name?.length > 3) {
      localStorage.setItem("name", name);
    }
  }, [name]);

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!valid) return;

    if (isEditing) {
      await updateGameSettings(
        code,
        {
          name,
          avatar: avatar.animal,
          color: avatar.color,
        },
        {
          answerTime,
          maxPlayers,
          password,
          voteTime,
        },
      );
      onClose?.();
    } else {
      const game = await newGame(
        {
          name,
          avatar: avatar.animal,
          color: avatar.color,
        },
        {
          answerTime,
          maxPlayers,
          password,
          voteTime,
          answerDecks: Object.keys(selectedAnswerDecks),
          questionDecks: Object.keys(selectedQuestionDecks),
        },
      );
      if (game?.game?.code) {
        window.location.assign(PATHS.GAME.PLAY(game.game.code));
      }
    }
  };

  const toggle =
    (setter: typeof setSelectedAnswerDecks) => (id: string) =>
      setter((selected) =>
        JSON.parse(
          JSON.stringify({
            ...selected,
            [id]: selected[id] ? undefined : true,
          }),
        ),
      );

  const allSelected =
    Object.keys(questionDeckMap).length ===
      Object.keys(selectedQuestionDecks).length &&
    Object.keys(answerDeckMap).length ===
      Object.keys(selectedAnswerDecks).length;

  const deckColumns = [
    {
      key: "question",
      title: "Perguntas",
      dark: true,
      total: totalQuestionCardsCount,
      min: 10,
      map: questionDeckMap,
      selected: selectedQuestionDecks,
      onToggle: toggle(setSelectedQuestionDecks),
    },
    {
      key: "answer",
      title: "Respostas",
      dark: false,
      total: totalAnswerCardsCount,
      min: 50,
      map: answerDeckMap,
      selected: selectedAnswerDecks,
      onToggle: toggle(setSelectedAnswerDecks),
    },
  ];

  const timeOptions = [
    <MenuItem key="0" value={0}>Ilimitado</MenuItem>,
    <MenuItem key="0.5" value={0.5}>30 segundos</MenuItem>,
    ...new Array(5).fill(null).map((_, i) => (
      <MenuItem key={i} value={i + 1}>
        {i + 1} minuto{i ? "s" : ""}
      </MenuItem>
    )),
  ];

  return (
    <ModalCard
      open={visible}
      onClose={handleClose}
      title={isEditing ? "Configurações" : "Nova partida"}
      icon={isEditing ? "settings" : "create"}
      tone={isEditing ? "night" : "gold"}
      width={isEditing ? 480 : 720}
      actions={
        <>
          <Button onClick={handleClose} variant="text">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress color="inherit" size={16} />
              ) : (
                <Icon name={isEditing ? "save" : "play"} />
              )
            }
            disabled={!valid}
          >
            {isEditing ? "Salvar" : "Criar partida"}
          </Button>
        </>
      }
    >
      {!!error?.message && <Alert severity="error">{error.message}</Alert>}

      <TextField
        label="Seu nome"
        variant="outlined"
        value={name}
        fullWidth
        error={!!name.length && name.length < 4}
        helperText={name.length < 4 ? "Pelo menos 4 letras" : undefined}
        onChange={(event) => setName(event.target.value)}
        required
      />

      <PlayerIconSelector onChange={setAvatar} />

      {(isAdmin || !isEditing) && (
        <>
          <SectionTitle>
            <Icon name="settings" />
            Regras da sala
          </SectionTitle>

          <TwoUp>
            <TextField
              label="Máximo de jogadores"
              variant="outlined"
              value={maxPlayers}
              fullWidth
              datatype="number"
              type="number"
              error={maxPlayers < 1 || maxPlayers > 15}
              helperText="De 1 a 15"
              onChange={(event) => setMaxPlayers(+event.target.value)}
              required
            />

            <TextField
              label="Senha (opcional)"
              variant="outlined"
              value={password}
              fullWidth
              error={!!password.length && password.length < 4}
              helperText="Vazio = sala aberta"
              onChange={(event) => setPassword(event.target.value)}
            />
          </TwoUp>

          <TwoUp>
            <FormControl fullWidth required>
              <InputLabel id="answer-time-label" required>
                Tempo pra responder
              </InputLabel>
              <Select
                label="Tempo pra responder"
                value={answerTime}
                onChange={(event) => setAnswerTime(+event.target.value)}
                labelId="answer-time-label"
                disabled={loading}
                variant="outlined"
                required
              >
                {timeOptions}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel id="vote-time-label" required>
                Tempo pra votar
              </InputLabel>
              <Select
                label="Tempo pra votar"
                value={voteTime}
                onChange={(event) => setVoteTime(+event.target.value)}
                labelId="vote-time-label"
                disabled={loading}
                variant="outlined"
                required
              >
                {timeOptions}
              </Select>
            </FormControl>
          </TwoUp>
        </>
      )}

      {!isEditing && (
        <>
          <SectionTitle>
            <Icon name="cards" />
            Baralhos
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                if (allSelected) {
                  setSelectedQuestionDecks({});
                  setSelectedAnswerDecks({});
                } else {
                  resetDecks();
                }
              }}
            >
              {allSelected ? "Limpar" : "Todos"}
            </Button>
          </SectionTitle>

          <DecksColumnsContainer>
            {deckColumns.map((column) => (
              <DeckColumn key={column.key}>
                <DeckColumnHeader $dark={column.dark}>
                  {column.title}
                  <Tag $tone={column.total < column.min ? "cherry" : "teal"}>
                    {column.total} cartas
                    {column.total < column.min && ` (mín. ${column.min})`}
                  </Tag>
                </DeckColumnHeader>
                <ul>
                  {decks?.map(
                    ({ id, name, description }) =>
                      !!column.map[id] && (
                        <li key={id}>
                          <DeckOption
                            type="button"
                            role="checkbox"
                            aria-checked={!!column.selected[id]}
                            $selected={!!column.selected[id]}
                            onClick={() => column.onToggle(id)}
                          >
                            <DeckCheck $selected={!!column.selected[id]}>
                              {!!column.selected[id] && (
                                <Icon name="check" size={14} strokeWidth={3.5} />
                              )}
                            </DeckCheck>
                            <span>
                              <b>{name}</b>
                              {!!description && <small>{description}</small>}
                            </span>
                            <Tag>{column.map[id]}</Tag>
                          </DeckOption>
                        </li>
                      ),
                  )}
                </ul>
              </DeckColumn>
            ))}
          </DecksColumnsContainer>
        </>
      )}
    </ModalCard>
  );
};

export default GameSettingsModal;
